/**
 * 価格計算システム - エントリーポイント
 * @version 2.1.0
 *
 * このファイルは以下のモジュールを統合して初期化します:
 * - price-data-indexer.js - 価格データインデックス（高速検索）
 * - price-calculator-core.js - コア初期化
 * - price-calculator-modals.js - モーダル管理
 * - price-calculator-handlers.js - 選択ハンドラー
 * - price-calculator-ui.js - UI更新
 * - price-calculator-variant-selectors.js - バリエーション選択UI
 * - price-calculator-booklet.js - 無線綴じ冊子
 *
 * 読み込み順序（Liquidテンプレート）:
 * 1. price-data-indexer.js
 * 2. price-calculator-core.js
 * 3. price-calculator-modals.js
 * 4. price-calculator-handlers.js
 * 5. price-calculator-ui.js
 * 6. price-calculator-booklet.js
 * 7. price-calculator-variant-selectors.js
 * 8. price-calculator-init.js （このファイル）
 */
(function() {
  'use strict';

  /**
   * 初期化設定
   */
  const InitConfig = {
    initialRenderDelay: 100,
    groupedOptionsInitDelay: 500,
    groupedOptionsRetryDelay: 200,
    maxGroupedOptionsRetries: 10
  };

  /**
   * 価格計算機を初期化
   */
  function initializePriceCalculator() {
    // コアモジュールで初期化
    const Core = window.PriceCalculator?.Core;
    if (!Core) {
      console.error('[Init] PriceCalculator.Core module not loaded');
      showFallbackError('価格計算モジュールの読み込みに失敗しました。');
      return;
    }

    if (!Core.initialize()) {
      showFallbackError('価格データが見つかりません。');
      return;
    }

    // 既に初期化済みの場合はスキップ
    if (window._priceCalculatorInitialized) {
      return;
    }

    try {
      // 並び順マスタを同期的に適用（Liquidで埋め込み済み）
      applyValueOrder();

      // 遅延ロードモードの場合、APIデータの読み込み完了を待つ
      if (window.needsLazyLoad && window.priceDataLoadPromise) {
        console.log('[Init] Lazy load mode - waiting for API data...');

        // 価格表にローディング表示
        const priceTableContainer = document.getElementById('price-table-container');
        if (priceTableContainer && window.PaginatedPriceTableGenerator) {
          const tempGenerator = new window.PaginatedPriceTableGenerator(priceTableContainer, null);
          tempGenerator.showLoading('価格データを読み込み中...');
        } else if (priceTableContainer) {
          // PaginatedPriceTableGeneratorが未ロードの場合は直接HTMLを設定
          priceTableContainer.innerHTML = `
            <div class="price-table-loading" style="text-align: center; padding: 40px 20px;">
              <div style="
                width: 40px;
                height: 40px;
                margin: 0 auto 16px;
                border: 3px solid #f3f3f3;
                border-top: 3px solid #333;
                border-radius: 50%;
                animation: price-table-spin 1s linear infinite;
              "></div>
              <div style="font-size: 14px; color: #666;">価格データを読み込み中...</div>
              <style>
                @keyframes price-table-spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              </style>
            </div>
          `;
        }

        window.priceDataLoadPromise.then(() => {
          // reinitializePriceCalculatorが既に呼ばれている場合はスキップ
          if (window._lazyLoadDataReady && window._priceCalculatorInitialized) {
            console.log('[Init] Already initialized by reinitializePriceCalculator, skipping');
            return;
          }

          // Liquidテンプレートで処理されたデータがある場合は使用
          if (window._lazyLoadPriceDataResult && !window._lazyLoadDataReady) {
            console.log('[Init] Using pre-processed price data from Liquid template');
            reinitializePriceCalculator(window._lazyLoadPriceDataResult);
            return;
          }

          console.log('[Init] API data loaded, performing initial render');
          setTimeout(() => {
            // 再度チェック（タイミングの問題を防ぐ）
            if (window._priceCalculatorInitialized) {
              console.log('[Init] Already initialized, skipping performInitialRender');
              return;
            }
            performInitialRender();
            window._priceCalculatorInitialized = true;
          }, InitConfig.initialRenderDelay);
        }).catch(error => {
          console.error('[Init] Error loading API data:', error);
          // フォールバック: APIエラー時でも初期化を試行
          setTimeout(() => {
            if (window._priceCalculatorInitialized) return;
            performInitialRender();
            window._priceCalculatorInitialized = true;
          }, InitConfig.initialRenderDelay);
        });
      } else {
        // 通常モード: 即座に初期化
        setTimeout(() => {
          performInitialRender();
          window._priceCalculatorInitialized = true;
        }, InitConfig.initialRenderDelay);
      }

    } catch (error) {
      console.error('[Init] Error during initialization:', error);
      showFallbackError('価格表の読み込みに失敗しました。ページを再読み込みしてください。');
    }
  }

  /**
   * 並び順マスタを適用（同期処理）
   * データはLiquidテンプレートで既に埋め込み済み
   */
  function applyValueOrder() {
    const ValueOrder = window.PriceCalculator?.ValueOrder;
    if (ValueOrder && ValueOrder.isLoaded()) {
      // Shopifyオプションをソート
      if (window.productData?.shopify_options) {
        window.productData.shopify_options = ValueOrder.sortShopifyOptionValues(window.productData.shopify_options);
      }
    }
  }

  /**
   * 初期レンダリングを実行
   */
  function performInitialRender() {

    // 価格データインデックスを構築（データが既にある場合）
    buildPriceDataIndex();

    // バリエーションセレクターの初期化
    if (typeof window.initializeVariantSelectors === 'function') {
      window.initializeVariantSelectors();
    }

    // UIの更新
    if (typeof window.updateUI === 'function') {
      window.updateUI();
    }

    if (typeof window.updateSelectorsFromData === 'function') {
      window.updateSelectorsFromData();
    }

    if (typeof window.updatePriceTable === 'function') {
      window.updatePriceTable();
    }

    // 初期表示の設定
    updateInitialDisplays();

    // 商品ヘッダーの更新
    if (typeof window.updateGoodsHeader === 'function') {
      window.updateGoodsHeader();
    }

    // 段階的初期化: バックグラウンドで残りのデータを処理
    scheduleBackgroundProcessing();

  }

  /**
   * バックグラウンドで残りの価格データを処理
   */
  function scheduleBackgroundProcessing() {
    const variantMetafields = window._variantMetafieldsForBackground;
    if (!variantMetafields || !window.processPriceData || !window.shopifyOptions) {
      return;
    }

    // バックグラウンド処理フラグをクリア
    window._variantMetafieldsForBackground = null;

    // requestIdleCallback または setTimeout でバックグラウンド処理
    const processRemainingData = () => {
      console.log('[Init] Background processing: Processing remaining variants...');
      const startTime = performance.now();

      // フィルタリングなしで全データを処理
      const fullResult = window.processPriceData(variantMetafields, window.shopifyOptions, {});

      // 既存のデータとマージ
      if (window.productData && window.productData.price_matrix) {
        const existingPriceData = window.productData.price_matrix.price_data || [];
        const existingVariations = window.productData.price_matrix.variations || {};

        // 新しいデータをマージ（重複を避ける）
        const existingKeys = new Set(existingPriceData.map(p =>
          `${p.variation_code}_${p.quantity}_${p.delivery_time}_${p.page_count || ''}`
        ));

        const newPriceData = fullResult.variantPriceData.filter(p => {
          const key = `${p.variation_code}_${p.quantity}_${p.delivery_time}_${p.page_count || ''}`;
          return !existingKeys.has(key);
        });

        // マージしたデータで更新
        window.productData.price_matrix = {
          variations: { ...existingVariations, ...fullResult.variationsData },
          price_data: [...existingPriceData, ...newPriceData]
        };

        // インデックスを再構築
        buildPriceDataIndex();

        const endTime = performance.now();
        console.log(`[Init] Background processing completed in ${(endTime - startTime).toFixed(2)}ms`, {
          totalPriceData: window.productData.price_matrix.price_data.length,
          addedEntries: newPriceData.length,
          totalVariations: Object.keys(window.productData.price_matrix.variations).length
        });
      }
    };

    // requestIdleCallback がサポートされている場合は使用
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(processRemainingData, { timeout: 5000 });
    } else {
      // フォールバック: setTimeoutで遅延実行
      setTimeout(processRemainingData, 500);
    }
  }

  /**
   * 初期表示を更新
   */
  function updateInitialDisplays() {
    if (!window.priceCalculator || !window.priceCalculator.currentSelection) {
      return;
    }

    const selection = window.priceCalculator.currentSelection;

    // 用紙表示
    const paperDisplay = document.getElementById('paper-display');
    if (paperDisplay && selection.paper) {
      paperDisplay.textContent = selection.paper;
    }

    // 印刷色表示
    const printColorDisplay = document.getElementById('print-color-display');
    if (printColorDisplay && selection.printColor) {
      printColorDisplay.textContent = selection.printColor;
    }

    // カラーオプションの表示/非表示を更新
    if (window.productOptionsManager && typeof window.productOptionsManager.updateColorOptionVisibility === 'function') {
      const initialPrintColor = selection.printColor;
      if (initialPrintColor) {
        window.productOptionsManager.updateColorOptionVisibility(initialPrintColor);
      }
    }
  }

  /**
   * フォールバックエラーを表示
   * @param {string} message - エラーメッセージ
   */
  function showFallbackError(message) {
    const container = document.getElementById('price-table-container');
    if (container) {
      container.innerHTML = `<div class="price-table-loading"><p style="color: #d32f2f;">${message}</p></div>`;
    }
  }

  /**
   * 価格データインデックスを構築
   */
  function buildPriceDataIndex() {
    const priceData = window.productData?.price_matrix?.price_data || [];
    if (priceData.length > 0 && window.PriceDataIndexer) {
      window.priceDataIndexer = new window.PriceDataIndexer(priceData);
    }
  }

  /**
   * ページ範囲オプションを有効な価格データがあるもののみにフィルタリング
   * price_matrixが空（variations: {}）のページ範囲は除外する
   * @param {Array} variantMetafields - バリアントメタフィールドの配列
   */
  function filterPageRangeOptionsByPriceData(variantMetafields) {
    console.log('[Init] filterPageRangeOptionsByPriceData called');

    // ページ範囲オプションがない場合はスキップ
    if (!window.pageRangeOptions || !Array.isArray(window.pageRangeOptions)) {
      console.log('[Init] No pageRangeOptions, skipping filter');
      return;
    }

    const metafields = variantMetafields || window._lazyLoadVariantMetafields || window.variantMetafields;
    if (!metafields || metafields.length === 0) {
      console.log('[Init] No metafields, skipping filter');
      return;
    }

    console.log('[Init] Metafields count:', metafields.length);

    // Shopifyオプションから「ページ範囲」のインデックスを取得
    const shopifyOptions = window.productData?.shopify_options || [];
    const optionNames = shopifyOptions.map(opt => opt.name);
    const pageRangeIndex = optionNames.indexOf('ページ範囲');

    console.log('[Init] shopifyOptions:', optionNames, 'pageRangeIndex:', pageRangeIndex);

    if (pageRangeIndex === -1) {
      console.log('[Init] No ページ範囲 option found, skipping filter');
      return;
    }

    // 有効な価格データがあるページ範囲を収集
    const validPageRanges = new Set();
    const emptyPageRanges = new Set();

    metafields.forEach(vm => {
      // ページ範囲の値を取得（option1またはoption2）
      const pageRange = pageRangeIndex === 0 ? vm.option1 : vm.option2;
      if (!pageRange) return;

      // price_matrixが有効かチェック
      const matrix = vm.matrixJson;
      if (matrix && matrix.variations) {
        const variationKeys = Object.keys(matrix.variations);
        // variations内に実際のデータがあるかチェック
        const hasData = variationKeys.some(key => {
          const entries = matrix.variations[key];
          return Array.isArray(entries) && entries.length > 0;
        });
        if (hasData) {
          validPageRanges.add(pageRange);
        } else {
          emptyPageRanges.add(pageRange);
        }
      } else {
        emptyPageRanges.add(pageRange);
      }
    });

    console.log('[Init] Valid page ranges:', Array.from(validPageRanges));
    console.log('[Init] Empty page ranges:', Array.from(emptyPageRanges));

    // 元のページ範囲オプションをフィルタリング
    const originalCount = window.pageRangeOptions.length;
    const filteredOptions = window.pageRangeOptions.filter(pr => validPageRanges.has(pr));

    if (filteredOptions.length !== originalCount) {
      console.log('[Init] Filtered pageRangeOptions:', {
        original: window.pageRangeOptions,
        valid: Array.from(validPageRanges),
        filtered: filteredOptions
      });
      window.pageRangeOptions = filteredOptions;
    }
  }

  /**
   * 価格計算システムを再初期化（遅延ロード後に呼び出す）
   * @param {Object} priceDataResult - processPriceDataの結果
   * @param {Array} variantMetafields - APIから取得したバリアントメタフィールド（オプション）
   */
  function reinitializePriceCalculator(priceDataResult, variantMetafields) {

    if (!priceDataResult || !priceDataResult.variantPriceData || priceDataResult.variantPriceData.length === 0) {
      console.error('[Init] No price data provided for reinitialization');
      return;
    }

    // window.productDataを更新
    if (window.productData) {
      window.productData.price_matrix = {
        variations: priceDataResult.variationsData || {},
        price_data: priceDataResult.variantPriceData
      };
    }

    // availableVariantCombinationsを再構築（Lazy Loadで取得した全バリアントデータから）
    // Liquidの100件制限を超えるバリアントを含める
    // variantMetafieldsがある場合はそれを使用（より正確）
    const metafields = variantMetafields || window._lazyLoadVariantMetafields;
    rebuildAvailableVariantCombinations(priceDataResult.variantPriceData, metafields);

    // ページ範囲オプションをフィルタリング（有効な価格データがあるもののみ）
    filterPageRangeOptionsByPriceData(metafields);

    // 価格データインデックスを構築（高速検索用）
    buildPriceDataIndex();

    // 遅延ロード完了フラグを設定
    window._lazyLoadDataReady = true;

    // 既存の価格計算インスタンスを破棄
    console.log('[Init] Destroying existing priceCalculator:', !!window.priceCalculator);
    if (window.priceCalculator) {
      window.priceCalculator = null;
    }
    window._priceCalculatorInitialized = false;
    // Bookletの再初期化を許可
    window._bookletInitialized = false;

    // 価格計算システムを再初期化
    const Core = window.PriceCalculator?.Core;
    if (Core) {
      if (Core.initialize()) {

        // UIを更新（Bookletの再初期化はスキップ）
        setTimeout(function() {
          performInitialRenderAfterLazyLoad();
          window._priceCalculatorInitialized = true;
        }, InitConfig.initialRenderDelay);
      } else {
        console.error('[Init] Failed to reinitialize Core');
      }
    } else {
      console.error('[Init] PriceCalculator.Core not available for reinitialization');
    }
  }

  /**
   * 遅延ロード後の初期レンダリング（Booklet再初期化をスキップ）
   */
  function performInitialRenderAfterLazyLoad() {
    const startTime = performance.now();
    let lastTime = startTime;

    const logTiming = (label) => {
      const now = performance.now();
      console.log(`[Init Timing] ${label}: ${(now - lastTime).toFixed(2)}ms (total: ${(now - startTime).toFixed(2)}ms)`);
      lastTime = now;
    };

    // バリエーションセレクターの初期化（Booklet.initializeは_lazyLoadDataReadyフラグでスキップされる）
    if (typeof window.initializeVariantSelectors === 'function') {
      window.initializeVariantSelectors();
    }
    logTiming('initializeVariantSelectors');

    // UIの更新
    if (typeof window.updateUI === 'function') {
      window.updateUI();
    }
    logTiming('updateUI');

    if (typeof window.updateSelectorsFromData === 'function') {
      window.updateSelectorsFromData();
    }
    logTiming('updateSelectorsFromData');

    if (typeof window.updatePriceTable === 'function') {
      window.updatePriceTable();
    }
    logTiming('updatePriceTable');

    // 初期表示の設定
    updateInitialDisplays();
    logTiming('updateInitialDisplays');

    // 商品ヘッダーの更新
    if (typeof window.updateGoodsHeader === 'function') {
      window.updateGoodsHeader();
    }
    logTiming('updateGoodsHeader');

    // モバイルセレクターの初期化（lazy load完了後に再実行）
    if (typeof window.initializeMobilePaperAndColorSelectors === 'function') {
      window.initializeMobilePaperAndColorSelectors();
    }
    if (typeof window.updateMobileSelectorsFromData === 'function') {
      window.updateMobileSelectorsFromData();
    }
    logTiming('mobileSelectorInit');

    // 色タイプセレクターの再初期化（lazy load完了後に再実行）
    if (window.ColorTypeSelector && typeof window.ColorTypeSelector.reload === 'function') {
      window.ColorTypeSelector.reload();
    }
    logTiming('colorTypeSelectorReload');

    console.log(`[Init] performInitialRenderAfterLazyLoad completed in ${(performance.now() - startTime).toFixed(2)}ms`);
  }

  /**
   * availableVariantCombinationsを再構築（Lazy Load後用）
   * variantMetafieldsのoption1/option2から用紙・印刷色数の組み合わせを抽出
   * @param {Array} variantPriceData - processPriceDataの結果のvariantPriceData（フォールバック用）
   * @param {Array} variantMetafields - APIから取得したバリアントメタフィールド（推奨）
   */
  function rebuildAvailableVariantCombinations(variantPriceData, variantMetafields) {
    // ★ paper_selector_mode チェック（統合モードはBookletモジュールが処理）
    const paperSelectorMode = window.productData?.paper_selector_mode || window._paper_selector_mode;
    if (paperSelectorMode === 'unified' || paperSelectorMode === 'monochrome_unified') {
      console.log('[Init] Unified paper mode detected - skipping standard combination rebuild');
      // 空の組み合わせデータを設定（Bookletモジュールが独自に管理）
      const emptyCombo = {
        byPaper: {},
        byPrintColor: {},
        withPriceByPaper: {},
        withPriceByPrintColor: {},
        isUnifiedMode: true
      };
      window.availableVariantCombinations = emptyCombo;
      if (window.productData) {
        window.productData.availableVariantCombinations = emptyCombo;
      }
      return;
    }

    const byPaper = {};
    const byPrintColor = {};
    // 価格データがある組み合わせのみ（非表示化対応）
    const withPriceByPaper = {};
    const withPriceByPrintColor = {};
    const normalize = window.normalizeString || ((str) => str);

    // Shopifyオプション名を取得（option1/option2がどのオプションに対応するか）
    const shopifyOptions = window.productData?.shopify_options || [];
    const optionNames = shopifyOptions.map(opt => opt.name);
    const paperOptionIndex = optionNames.indexOf('用紙');
    const printColorOptionIndex = optionNames.indexOf('印刷色数');

    // variantMetafieldsがある場合は、option1/option2から直接組み合わせを抽出
    // これにより、100件制限を回避して全バリアントの組み合わせを取得できる
    if (Array.isArray(variantMetafields) && variantMetafields.length > 0) {
      console.log('[Init] Rebuilding combinations from variantMetafields:', variantMetafields.length, 'variants');

      variantMetafields.forEach(vm => {
        // option1, option2 からオプション値を取得
        // Shopifyのオプション順序に基づいて用紙と印刷色数を特定
        let paper = null;
        let printColor = null;

        if (paperOptionIndex === 0) {
          paper = vm.option1;
          printColor = vm.option2;
        } else if (paperOptionIndex === 1) {
          paper = vm.option2;
          printColor = vm.option1;
        } else if (printColorOptionIndex === 0) {
          printColor = vm.option1;
          paper = vm.option2;
        } else if (printColorOptionIndex === 1) {
          printColor = vm.option2;
          paper = vm.option1;
        } else {
          // 用紙/印刷色数オプションが見つからない場合はデフォルト
          // チラシ商品: option1=印刷色数, option2=用紙 の場合が多い
          printColor = vm.option1;
          paper = vm.option2;
        }

        if (paper && printColor) {
          // byPaper: 用紙 -> 印刷色数リスト（全バリアント）
          if (!byPaper[paper]) {
            byPaper[paper] = [];
          }
          if (!byPaper[paper].includes(printColor)) {
            byPaper[paper].push(printColor);
          }

          // byPrintColor: 印刷色数 -> 用紙リスト（全バリアント）
          if (!byPrintColor[printColor]) {
            byPrintColor[printColor] = [];
          }
          if (!byPrintColor[printColor].includes(paper)) {
            byPrintColor[printColor].push(paper);
          }

          // 価格データがある場合のみ withPriceByPaper/withPriceByPrintColor に追加
          // matrixJson が null でない = 価格データがある（非表示化されていない）
          const hasPrice = vm.matrixJson !== null && vm.matrixJson !== undefined;
          if (hasPrice) {
            if (!withPriceByPaper[paper]) {
              withPriceByPaper[paper] = [];
            }
            if (!withPriceByPaper[paper].includes(printColor)) {
              withPriceByPaper[paper].push(printColor);
            }

            if (!withPriceByPrintColor[printColor]) {
              withPriceByPrintColor[printColor] = [];
            }
            if (!withPriceByPrintColor[printColor].includes(paper)) {
              withPriceByPrintColor[printColor].push(paper);
            }
          }
        }
      });
    } else if (Array.isArray(variantPriceData) && variantPriceData.length > 0) {
      // フォールバック: variantPriceDataのvariation_codeから抽出
      // variantPriceDataは既に価格データがあるエントリのみなので、withPriceにも追加
      console.log('[Init] Rebuilding combinations from variantPriceData (fallback):', variantPriceData.length, 'entries');

      variantPriceData.forEach(entry => {
        const code = entry.variation_code;
        if (!code || code === '標準') return;

        const parts = code.split('_');
        if (parts.length >= 2) {
          const paper = parts[0];
          const printColor = parts.slice(1).join('_');

          // 全バリアント用
          if (!byPaper[paper]) {
            byPaper[paper] = [];
          }
          if (!byPaper[paper].includes(printColor)) {
            byPaper[paper].push(printColor);
          }

          if (!byPrintColor[printColor]) {
            byPrintColor[printColor] = [];
          }
          if (!byPrintColor[printColor].includes(paper)) {
            byPrintColor[printColor].push(paper);
          }

          // 価格データがある用（variantPriceDataは既に価格があるデータのみ）
          if (!withPriceByPaper[paper]) {
            withPriceByPaper[paper] = [];
          }
          if (!withPriceByPaper[paper].includes(printColor)) {
            withPriceByPaper[paper].push(printColor);
          }

          if (!withPriceByPrintColor[printColor]) {
            withPriceByPrintColor[printColor] = [];
          }
          if (!withPriceByPrintColor[printColor].includes(paper)) {
            withPriceByPrintColor[printColor].push(paper);
          }
        }
      });
    } else {
      console.warn('[Init] No data for rebuilding variant combinations');
      return;
    }

    // Shopifyオプションも考慮（抽出漏れがある場合の補完）
    const printColorOption = shopifyOptions.find(opt => opt.name === '印刷色数');
    if (printColorOption && printColorOption.values) {
      printColorOption.values.forEach(color => {
        if (!byPrintColor[color]) {
          const normalizedColor = normalize(color);
          const matchingKey = Object.keys(byPrintColor).find(k => normalize(k) === normalizedColor);
          if (!matchingKey) {
            byPrintColor[color] = [];
          }
        }
      });
    }

    // 新しい組み合わせデータを作成
    const newCombinations = {
      byPaper: byPaper,
      byPrintColor: byPrintColor,
      // 価格データがある組み合わせのみ（非表示化対応）
      withPriceByPaper: withPriceByPaper,
      withPriceByPrintColor: withPriceByPrintColor
    };

    // window.availableVariantCombinationsを更新
    window.availableVariantCombinations = newCombinations;

    // window.productData.availableVariantCombinationsも更新（フォールバック参照対策）
    if (window.productData) {
      window.productData.availableVariantCombinations = newCombinations;
    }

    console.log('[Init] Rebuilt availableVariantCombinations:', {
      allPapersCount: Object.keys(byPaper).length,
      papersWithPriceCount: Object.keys(withPriceByPaper).length,
      printColorsCount: Object.keys(byPrintColor).length,
      hiddenPapers: Object.keys(byPaper).filter(p => !withPriceByPaper[p])
    });
  }

  // グローバルに公開（遅延ロードから呼び出せるように）
  window.reinitializePriceCalculator = reinitializePriceCalculator;
  window.buildPriceDataIndex = buildPriceDataIndex;
  window.rebuildAvailableVariantCombinations = rebuildAvailableVariantCombinations;

  /**
   * APIからオプションデータを取得
   * @param {string} handle - 商品ハンドル
   * @returns {Promise<Object|null>} オプションデータ
   */
  async function fetchOptionsFromAPI(handle) {
    if (!handle) {
      console.warn('[Init] Product handle not available for API fetch');
      return null;
    }

    // APIエンドポイントを取得（ローカル開発時はlocalhost:3003を使用）
    // ローカル開発モードの判定:
    // 1. shopify theme dev使用中（preview_theme_idがsessionStorageに保存される）
    // 2. localhost/127.0.0.1からアクセス
    // ※ それ以外は全て本番API（独自ドメインでも.myshopify.comでも同様）
    const urlParams = new URLSearchParams(window.location.search);
    const hasPreviewThemeId = urlParams.has('preview_theme_id');

    // preview_theme_idがあれば、sessionStorageに保存（ブラウザを閉じるまで有効）
    if (hasPreviewThemeId) {
      sessionStorage.setItem('isThemeDev', 'true');
    }

    // API Base URL（theme.liquidで設定済み、localhost検出含む）
    const apiBaseUrl = window.API_BASE_URL;

    if (!apiBaseUrl) {
      console.warn('[Init] Options API base URL not configured');
      return null;
    }

    // [id]パラメータにはハンドルを渡す（APIは両方に対応）
    const apiUrl = `${apiBaseUrl}/api/storefront/products/${encodeURIComponent(handle)}/options`;
    console.log('[Init] Fetching options from API:', apiUrl);

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        cache: 'no-store', // キャッシュを無効化してリアルタイム反映
      });

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log(`[Init] API returned ${data.options?.length || 0} options`);
        return data;
      } else {
        console.warn('[Init] API returned error:', data.error);
        return null;
      }
    } catch (error) {
      console.warn('[Init] Failed to fetch options from API:', error.message);
      return null;
    }
  }

  /**
   * GroupedOptionsManagerの初期化
   */
  function initializeGroupedOptionsManager() {
    let retryCount = 0;

    // オプション読み込み中フラグを設定（カートバリデーション用）
    window._optionsLoading = true;

    async function tryInit() {
      retryCount++;

      // 商品ハンドルを取得
      const productHandle = window.productData?.handle;

      // APIからオプションを取得（初回のみ）
      if (retryCount === 1 && productHandle) {
        try {
          const apiData = await fetchOptionsFromAPI(productHandle);

          if (apiData && apiData.options && apiData.options.length > 0) {
            // APIから取得したデータでwindow.productDataを更新
            window.productData.product_options = apiData.options;
            window.productData.option_display_order = apiData.orderSettings;

            console.log('[Init] Updated productData with API options');

            // ProductOptionsManagerを再作成する必要がある場合
            if (window.productOptionsManager && window.ProductOptionsManager) {
              // 既存のallOptionsを更新
              window.productOptionsManager.allOptions = apiData.options;
              window.productOptionsManager.requiredOptions = apiData.requiredOptions || [];
              window.productOptionsManager.optionalOptions = apiData.optionalOptions || [];
              console.log('[Init] Updated ProductOptionsManager with API options');
            }
          }
        } catch (error) {
          console.warn('[Init] Error fetching options from API, falling back to Liquid data:', error);
        }
      }

      if (window.productOptionsManager) {
        const allOptions = window.productOptionsManager.allOptions;

        if (allOptions && Array.isArray(allOptions) && allOptions.length > 0 && window.GroupedOptionsManager) {
          try {
            window.groupedOptionsManager = new window.GroupedOptionsManager(allOptions);
            console.log('[Init] GroupedOptionsManager initialized with', allOptions.length, 'options');
          } catch (error) {
            console.error('[Init] Error initializing GroupedOptionsManager:', error);
          }
          // オプション読み込み完了
          window._optionsLoading = false;
          return;
        } else if (allOptions !== undefined) {
          // オプションがない商品の場合も完了とみなす
          window._optionsLoading = false;
          return;
        }
      }

      if (retryCount >= InitConfig.maxGroupedOptionsRetries) {
        // リトライ上限到達、読み込み完了（失敗）
        window._optionsLoading = false;
        console.warn('[Init] Max retries reached for GroupedOptionsManager');
        return;
      }

      setTimeout(tryInit, InitConfig.groupedOptionsRetryDelay);
    }

    setTimeout(tryInit, InitConfig.groupedOptionsInitDelay);
  }

  // ==========================================
  // イベントリスナー
  // ==========================================

  // DOMContentLoadedで初期化
  document.addEventListener('DOMContentLoaded', function() {
    initializePriceCalculator();
  });

  // window.loadでの再試行
  window.addEventListener('load', function() {
    if (!window._priceCalculatorInitialized) {
      setTimeout(initializePriceCalculator, 1000);
    } else {
    }
  });

  // GroupedOptionsManagerの初期化
  initializeGroupedOptionsManager();

})();
